import {Button, makeStyles, Typography} from "@material-ui/core";
import {grey} from "@material-ui/core/colors";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {GridForm} from "ts/api/task";
import type {AssignedTaskFormValue} from "view/components/TasksList/types";
import Alert from "view/components/Alert";
import FfairPaper from "view/components/FfairPaper";
import Form from "view/components/Form";
import Grid from "view/components/GridResponseBlock/CustomizeTable/Components/Grid";
import FlexBox from "view/components/Layout/FlexBox";
import FfairModal from "view/components/modal/FfairModal";
import {T, useTranslate} from "@tolgee/react";

type GridPreviewProps = {
  task: AssignedTaskFormValue;
  // Open the drawer-owned response edit modal. "Continue Task" delegates to
  // this (the same opener the drawer's action panel uses) so editing reuses
  // the drawer's already-loaded state — no cold-refetch race, correct standId.
  onContinueResponse?: () => void;
};

type ModalStatuses = "none" | "preview";

function GridPreview({task, onContinueResponse}: GridPreviewProps) {
  const {t} = useTranslate();
  const form = useForm<GridForm>({
    defaultValues: {
      grid: task?.grid,
      additionalRowNames: task?.additionalRowNames,
    },
  });

  useEffect(() => {
    form.setValue("grid", task?.grid || []);
    form.setValue("additionalRowNames", task?.additionalRowNames);
  }, [task?.grid, form, task?.additionalRowNames]);

  const [modalStatus, setModalStatus] = useState<ModalStatuses>("none");

  const classes = useStyles();
  return (
    <>
      <FlexBox column gap={3}>
        <Alert severity="info">
          <Typography className={classes.savedDraft}>
            <T keyName="common.tasks.grid.draft_saved" />
          </Typography>
        </Alert>
        <FlexBox justifyContent="flex-end">
          <Button color="primary" onClick={() => setModalStatus("preview")}>
            <T keyName="common.tasks.grid.preview_draft" />
          </Button>
        </FlexBox>
      </FlexBox>
      {modalStatus === "preview" && (
        <FfairModal open onClose={() => setModalStatus("none")}>
          <FfairPaper className={classes.popupRoot}>
            <FlexBox column gap={2}>
              <FlexBox className={classes.taskTitle} alignCenter>
                <Typography>
                  {task.title} <span style={{color: grey[500]}}>-</span>
                </Typography>
                <Typography className={classes.taskTitleDraft}>
                  <T keyName="common.tasks.grid.draft_badge" />
                </Typography>
              </FlexBox>
              <FlexBox column gap={1}>
                <Typography className={classes.gridName}>
                  {t("common.tasks.grid.table_name", {
                    name: task.gridSchema?.name,
                  })}
                </Typography>
                <Form
                  form={form}
                  onSubmit={form.handleSubmit(() => null)}
                  className={classes.form}
                >
                  <Grid
                    viewOnly
                    isResponseMode
                    headCellSize="small"
                    task={task}
                  />
                </Form>
              </FlexBox>
              <FlexBox justifyContent="flex-end" alignCenter gap={3} mt={2}>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setModalStatus("none")}
                >
                  {t("common.action.close")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Hand off to the drawer's response edit modal instead of
                    // mounting our own (which refetched from cold).
                    setModalStatus("none");
                    onContinueResponse?.();
                  }}
                >
                  <T keyName="common.tasks.grid.continue_task" />
                </Button>
              </FlexBox>
            </FlexBox>
          </FfairPaper>
        </FfairModal>
      )}
    </>
  );
}

const useStyles = makeStyles(({spacing, palette}) => ({
  savedDraft: {
    lineHeight: "150%",
  },
  popupRoot: {
    maxWidth: "1048px",
    minWidth: "896px",
    padding: spacing(3),
    background: palette.otherElementsBackground.main,
    color: palette.contrastText.main,
    borderColor: palette.separator.main,
  },
  form: {
    padding: 0,
    background: palette.otherElementsBackground.main,
  },
  taskTitle: {
    "& *": {
      lineHeight: "150%",
      fontSize: "24px",
      fontWeight: 500,
    },
  },
  taskTitleDraft: {
    color: grey[500],
    lineHeight: "120%",
    letterSpacing: "2.4px",
  },
  gridName: {
    color: grey[600],
    fontSize: "14px",
    lineHeight: "19px",
    letterSpacing: "0.15px",
  },
}));

export default GridPreview;
